/**
 * @file Jobs list view — both Sales and Jobs pipelines with collapsible stages
 */

import { getSalesPipelineJobs, getJobsPipelineJobs, getSalesStages, getStages } from '@services/jobs.js';
import { renderJobCard } from '@components/job-card.js';
import { push } from '@core/router.js';

let _activeFilter = 'All';
let _searchTerm = '';
let _collapsedStages = {};

const SALES_STAGE_BADGES = {
  'New': 'badge-blue',
  'Contacted': 'badge-purple',
  'Quoted': 'badge-gold',
  'Proposal Sent': 'badge-orange',
  'Booked': 'badge-green',
  'Proposal Rejected': 'badge-red',
};

const JOBS_STAGE_BADGES = {
  'Accepted': 'badge-blue',
  'Scheduled': 'badge-purple',
  'In Progress': 'badge-gold',
  'Completed': 'badge-green',
  'Paid & Closed': 'badge-gray',
};

function render() {
  const salesJobs = filterJobs(getSalesPipelineJobs());
  const jobsJobs = filterJobs(getJobsPipelineJobs());

  return `
    <div class="view-header">
      <h1>Jobs</h1>
    </div>

    <div class="search-wrap">
      <span class="search-icon">\uD83D\uDD0D</span>
      <input type="text" id="jobs-search" placeholder="Search jobs..." value="${escHtml(_searchTerm)}" />
    </div>

    <div class="filter-chips" id="jobs-filters">
      ${['All', 'Active', 'Completed'].map(f =>
        `<span class="chip ${_activeFilter === f ? 'active' : ''}" data-filter="${f}">${f}</span>`
      ).join('')}
    </div>

    <div class="pipeline-section" id="sales-pipeline-section">
      <div class="pipeline-header">
        <h3>Sales Pipeline</h3>
        <span class="badge badge-gold">${salesJobs.length}</span>
      </div>
      ${renderPipelineStages(salesJobs, getSalesStages(), SALES_STAGE_BADGES, 'sales')}
    </div>

    <div class="pipeline-section" id="jobs-pipeline-section">
      <div class="pipeline-header">
        <h3>Jobs Pipeline</h3>
        <span class="badge badge-blue">${jobsJobs.length}</span>
      </div>
      ${renderPipelineStages(jobsJobs, getStages(), JOBS_STAGE_BADGES, 'jobs')}
    </div>
  `;
}

function filterJobs(jobs) {
  let filtered = jobs;

  // Filter by active/completed
  if (_activeFilter === 'Active') {
    filtered = filtered.filter(j =>
      j.stage !== 'Completed' &&
      j.stage !== 'Paid & Closed' &&
      j.stage !== 'Proposal Rejected'
    );
  } else if (_activeFilter === 'Completed') {
    filtered = filtered.filter(j =>
      j.stage === 'Completed' ||
      j.stage === 'Paid & Closed' ||
      j.stage === 'Proposal Rejected'
    );
  }

  // Filter by search
  if (_searchTerm) {
    const q = _searchTerm.toLowerCase();
    filtered = filtered.filter(j =>
      (j.customerName || j.name || '').toLowerCase().includes(q) ||
      (j.desc || '').toLowerCase().includes(q) ||
      (j.phone || '').toLowerCase().includes(q) ||
      (j.address || '').toLowerCase().includes(q) ||
      (j.stage || '').toLowerCase().includes(q)
    );
  }

  return filtered;
}

function renderPipelineStages(jobs, stages, badgeMap, pipelinePrefix) {
  if (jobs.length === 0) {
    return `<div class="empty-state" style="padding:20px;">
      <div class="empty-state-text" style="font-size:0.85rem;">No jobs found</div>
    </div>`;
  }

  // Group by stage
  const grouped = {};
  for (const stage of stages) {
    grouped[stage] = [];
  }
  for (const job of jobs) {
    if (!grouped[job.stage]) grouped[job.stage] = [];
    grouped[job.stage].push(job);
  }

  // Sort within each stage by date
  for (const stage of Object.keys(grouped)) {
    grouped[stage].sort((a, b) =>
      (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || '')
    );
  }

  let html = '';
  for (const stage of stages) {
    const stageJobs = grouped[stage] || [];
    if (stageJobs.length === 0) continue;

    const stageKey = `${pipelinePrefix}-${stage}`;
    const isCollapsed = _collapsedStages[stageKey];
    const badgeClass = badgeMap[stage] || 'badge-gray';

    html += `
      <div class="stage-group">
        <div class="stage-header" data-stage-key="${escHtml(stageKey)}">
          <div class="stage-header-left">
            <span class="stage-chevron${isCollapsed ? '' : ' open'}">\u203A</span>
            <span class="stage-name">${escHtml(stage)}</span>
            <span class="badge ${badgeClass}" style="margin-left:6px;">${stageJobs.length}</span>
          </div>
        </div>
        <div class="stage-cards${isCollapsed ? ' collapsed' : ''}">
          ${stageJobs.map(j => renderJobCard(j)).join('')}
        </div>
      </div>
    `;
  }

  return html;
}

function mount() {
  // Search input
  const search = document.getElementById('jobs-search');
  if (search) {
    search.addEventListener('input', (e) => {
      _searchTerm = e.target.value;
      refreshView();
    });
  }

  // Filter chips
  const filters = document.getElementById('jobs-filters');
  if (filters) {
    filters.addEventListener('click', (e) => {
      const chip = e.target.closest('.chip');
      if (!chip) return;
      _activeFilter = chip.dataset.filter;
      filters.querySelectorAll('.chip').forEach(c => c.classList.toggle('active', c.dataset.filter === _activeFilter));
      refreshView();
    });
  }

  // Stage header collapse toggles
  wireStageHeaders();

  // Job card taps
  wireJobTaps();
}

function wireStageHeaders() {
  document.querySelectorAll('.stage-header').forEach(header => {
    header.addEventListener('click', () => {
      const key = header.dataset.stageKey;
      _collapsedStages[key] = !_collapsedStages[key];
      const cards = header.nextElementSibling;
      const chevron = header.querySelector('.stage-chevron');
      if (cards) cards.classList.toggle('collapsed');
      if (chevron) chevron.classList.toggle('open');
    });
  });
}

function wireJobTaps() {
  document.querySelectorAll('.stage-cards').forEach(container => {
    container.addEventListener('click', (e) => {
      const card = e.target.closest('.job-card');
      if (card) push('job-detail', { jobId: card.dataset.jobId });
    });
  });
}

function refreshView() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function escHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
